/**
 * 001-catalogue-admin-screens T063 (discount-rules-wizard). Structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx — the
 * same source `pages/catalogue/categories/New.tsx` cites for its own "Parent category" picker,
 * which is where the `{"  ".repeat(c.depth)}` indentation idiom (FR-008) this component reuses
 * comes from.
 *
 * A `DiscountRuleRequest` target is one of five scopes (data-model §3): `all` carries no target;
 * `brand`/`category`/`subcategory`/`sku` each need one. `GET /categories/tree` returns categories
 * and sub-categories in the same `CategoryView[]`, told apart by `isSubcategory` — this component
 * filters that one list rather than calling the tree endpoint twice.
 *
 * `scope: "sku"` uses the shared `SkuPicker` (`listSkus` search) — GAP-1's pasted-id interim is gone.
 */
import type { Brand, Category, DiscountScope } from "../lib/services/catalog";
import { SelectField } from "./select-field";
import { SkuPicker } from "./catalog-pickers";

const SCOPE_LABEL: Record<DiscountScope, string> = {
  all: "All products",
  brand: "Brand",
  category: "Category",
  subcategory: "Sub-category",
  sku: "SKU",
};

const SCOPES: readonly DiscountScope[] = ["all", "brand", "category", "subcategory", "sku"];

export function ScopePicker({
  idPrefix,
  scope,
  scopeId,
  brands,
  categories,
  onScopeChange,
  onScopeIdChange,
  /** A rule loaded from `GET /tiers/{id}/rules` whose target no longer resolves (data-model §3) — kept selectable so saving the set doesn't silently rewrite it to whatever option happens to sit first. */
  unresolved,
  /** Submit-time "a target is required" check (FR-027's sibling rule) — rendered on the target field itself, not borrowed onto an unrelated column. */
  targetError,
}: {
  idPrefix: string;
  scope: DiscountScope;
  scopeId: string | null;
  brands: readonly Brand[];
  categories: readonly Category[];
  onScopeChange: (scope: DiscountScope) => void;
  onScopeIdChange: (scopeId: string | null) => void;
  unresolved?: { scopeName: string | null; scopeIssue: string | null };
  targetError?: string;
}) {
  const targets =
    scope === "category"
      ? categories.filter((c) => !c.isSubcategory)
      : scope === "subcategory"
        ? categories.filter((c) => c.isSubcategory)
        : [];

  return (
    <div className="flex flex-wrap items-start gap-3">
      <SelectField
        label="Scope"
        required
        name={`${idPrefix}-scope`}
        wrapperClassName="w-40 shrink-0"
        className="w-full"
        value={scope}
        onChange={(e) => {
          const next = e.target.value as DiscountScope;
          onScopeChange(next);
          onScopeIdChange(null);
        }}
      >
        {SCOPES.map((s) => (
          <option key={s} value={s}>
            {SCOPE_LABEL[s]}
          </option>
        ))}
      </SelectField>

      {scope === "all" ? (
        // Holds the target column so every row's Discount % lines up, and says why it is empty.
        <div className="min-w-56 flex-1">
          <span className="mb-1.5 block text-sm font-medium">Applies to</span>
          <p className="flex h-10 items-center rounded-md border border-dashed border-muted px-3 text-sm text-gray-500">
            Every product — no target needed
          </p>
        </div>
      ) : scope === "brand" ? (
        <SelectField
          label="Brand"
          required
          name={`${idPrefix}-target`}
          wrapperClassName="min-w-56 flex-1"
          className="w-full"
          error={targetError}
          value={scopeId ?? ""}
          onChange={(e) => onScopeIdChange(e.target.value || null)}
          disabled={brands.length === 0}
          disabledReason={brands.length === 0 ? "No brands available." : undefined}
        >
          <option value="">{brands.length === 0 ? "No brands available" : "Choose a brand…"}</option>
          {unresolved && scopeId && !brands.some((b) => b.id === scopeId) ? (
            <option value={scopeId}>{unresolved.scopeName ?? "Unnamed record"} — {unresolved.scopeIssue}</option>
          ) : null}
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </SelectField>
      ) : scope === "category" || scope === "subcategory" ? (
        <SelectField
          label={SCOPE_LABEL[scope]}
          required
          name={`${idPrefix}-target`}
          wrapperClassName="min-w-56 flex-1"
          className="w-full"
          error={targetError}
          value={scopeId ?? ""}
          onChange={(e) => onScopeIdChange(e.target.value || null)}
          disabled={targets.length === 0}
          disabledReason={targets.length === 0 ? `No ${SCOPE_LABEL[scope].toLowerCase()}s available.` : undefined}
        >
          <option value="">{targets.length === 0 ? `No ${SCOPE_LABEL[scope].toLowerCase()}s available` : `Choose a ${SCOPE_LABEL[scope].toLowerCase()}…`}</option>
          {unresolved && scopeId && !targets.some((c) => c.id === scopeId) ? (
            <option value={scopeId}>{unresolved.scopeName ?? "Unnamed record"} — {unresolved.scopeIssue}</option>
          ) : null}
          {targets.map((c) => (
            <option key={c.id} value={c.id}>
              {"  ".repeat(c.depth)}
              {c.name}
            </option>
          ))}
        </SelectField>
      ) : (
        <div className="min-w-56 flex-1">
          <SkuPicker value={scopeId ?? ""} onChange={(id) => onScopeIdChange(id || null)} error={targetError} required />
        </div>
      )}

      {unresolved ? (
        <p role="alert" className="mt-0.5 basis-full text-[13px] text-red">
          This rule&apos;s target no longer resolves — {unresolved.scopeIssue ?? "unknown reason"}. Choose a new
          target or remove the rule.
        </p>
      ) : null}
    </div>
  );
}
